import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { ValidationError } from "myzod";
import { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import { NotSerializedBookingInputs as BookingInputs, Frequence } from "../typings/booking-inputs";
import { LocationAvailability } from "../typings/booking-location";
import { BookingFilterInputs, BookingFilterObj } from "../typings/inputs-filter";
import { TeamMember } from "../typings/team-member";
import { BookingVehicleInfo } from "../../../components/FleetManagement/types/FleetManagement-row.types";
import type { BookingResourceInfo } from "../../Schedule/typings/booking-resource-info.types";

type BookingState = {
  inputs: BookingInputs;
  locations: {
    inventory: LocationAvailability[];
    isLoading: boolean;
    error?: AxiosError | ValidationError;
  };
  floors: {
    inventory: FloorAvailability[];
    isLoading: boolean;
    currentFloorIndex?: number;
    error?: AxiosError | ValidationError;
  };
};

const initialInputs = {
  frequence: Frequence.SINGLE,
  weekdays: {
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true,
    Sunday: true
  },
  automatedSeating: false,
  activityBasedBooking: false,
  timeSpanBigEnough: true
} as BookingInputs;

function createFreshBookingInputs(): BookingInputs {
  return {
    ...initialInputs,
    visitorRegistrations: []
  };
}

const initialState: BookingState = {
  inputs: createFreshBookingInputs(),
  locations: {
    inventory: [],
    isLoading: false
  },
  floors: {
    inventory: [],
    isLoading: false
  }
};

const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    setInputs(state, action: PayloadAction<Partial<BookingState["inputs"]>>) {
      state.inputs = { ...state.inputs, ...action.payload };
    },

    setBookingFilters(state, action: PayloadAction<Partial<BookingFilterObj["bookingFilters"]>>) {
      state.inputs.bookingFilters = { ...state.inputs.bookingFilters, ...action.payload };
      const floors = state.floors;
      if (floors.currentFloorIndex !== undefined && floors.inventory) {
        const currentFloor = floors.inventory[floors.currentFloorIndex];
        if (currentFloor) {
          state.inputs.floorInventoryId = currentFloor.id;
        }
      }
    },

    selectBookingLocation(
      state,
      action: PayloadAction<
        Pick<
          BookingState["inputs"],
          | "selectedLocation"
          | "locationName"
          | "isMobileWorking"
          | "timezone"
          | "automatedSeating"
          | "automatedSeatingObjects"
        >
      >
    ) {
      state.inputs = { ...state.inputs, ...action.payload };
    },

    selectSingleWorkplace(state, action: PayloadAction<TeamMember>) {
      state.inputs.usersBookedFor = [action.payload];
    },

    selectTeamMemberWorkplace(
      state,
      action: PayloadAction<{ index: number; placeInventoryId: number | undefined }>
    ) {
      const { placeInventoryId, index } = action.payload;

      const user = state.inputs.usersBookedFor?.[index];
      if (user) {
        user.bookingInventoryId = placeInventoryId;
      }
    },
    selectVehicleBooking(
      state,
      action: PayloadAction<{
        usersBookedFor: TeamMember;
        vehicleInfo: BookingVehicleInfo | undefined;
      }>
    ) {
      state.inputs.usersBookedFor = [action.payload.usersBookedFor];
      state.inputs.bookedVehicle = action.payload.vehicleInfo;
    },
    selectResourceBooking(
      state,
      action: PayloadAction<{
        usersBookedFor: TeamMember;
        resourceInfo: BookingResourceInfo | undefined;
        resourceKind: BookingResourceInfo["resourceKind"];
        locationInventoryId: number | null;
        locationName: string | null;
      }>
    ) {
      state.inputs.usersBookedFor = [action.payload.usersBookedFor];
      state.inputs.bookedResource = action.payload.resourceInfo;
      state.inputs.resourceKind = action.payload.resourceKind;
      state.inputs.orderedQty = 1;
      state.inputs.selectedLocation = action.payload.locationInventoryId;
      state.inputs.locationName = action.payload.locationName;
    },

    clearInputs(state) {
      state.inputs = createFreshBookingInputs();
    },
    clearFloors(state) {
      state.floors = {
        inventory: [],
        isLoading: false
      };
    },

    clearBookingFilters(state) {
      state.inputs.bookingFilters = undefined;
    },

    setLocations(state, action: PayloadAction<Partial<BookingState["locations"]>>) {
      state.locations = { ...state.locations, ...action.payload };
    },

    setFloors(state, action: PayloadAction<Partial<BookingState["floors"]>>) {
      state.floors = { ...state.floors, ...action.payload };
    },

    setUserBookingFilters(
      state,
      action: PayloadAction<{ userIndex: number; bookingFilters: BookingFilterInputs }>
    ) {
      const { userIndex, bookingFilters } = action.payload;
      if (state.inputs?.usersBookedFor?.[userIndex]) {
        state.inputs.usersBookedFor[userIndex].bookingFilters = {
          ...state.inputs.usersBookedFor[userIndex].bookingFilters,
          ...bookingFilters
        };
      } else {
        throw new Error(`No booking user on index ${userIndex}`);
      }
    }
  }
});

export default bookingSlice.reducer;
export const {
  setInputs,
  setLocations,
  setFloors,
  setBookingFilters,
  clearInputs,
  clearBookingFilters,
  clearFloors,
  selectBookingLocation,
  selectSingleWorkplace,
  selectTeamMemberWorkplace,
  selectVehicleBooking,
  selectResourceBooking,
  setUserBookingFilters
} = bookingSlice.actions;
